import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Row, Col, Spinner } from 'react-bootstrap';
import AdministrationDashboard from './AdministrationDashboard'; 
import Swal from 'sweetalert2';
import { GitBranch } from 'lucide-react';
import AsyncStorage from '@react-native-async-storage/async-storage'; 
import { API_URL, UPLOADS_API_URL } from './config';

const AdminCreateBranch = () => {
  const [branchData, setBranchData] = useState({
    branch_id: "",
    branch_name: "",
    branch_location: "",
    branch_location_notes: "",
    branch_city: "",
    company_id: "",
    company_name: "",
    enrolled_on: new Date().toISOString().split('T')[0],
    enrolled_by: "",
    phone: "",
    email: "",
    sync_interval: "",
    branch_reorder_level_kgs: "",
    auto_branch_reorder_status: false,
    inventory_level: "",
    inventory_storage_capacity: "",
    rems_subscription_id: "",
    transactions_volume_limit_kgs: "",
    expiry_date: ""
  });

  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    fetchCompanyDetails();
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${API_URL}/companysetup`);
      const data = await response.json();
      setCompanies(data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to fetch companies',
        icon: 'error'
      });
    }
  };

  const fetchCompanyDetails = async () => {
    try {
      const companyId = await AsyncStorage.getItem('company_id');
      const companyName = await AsyncStorage.getItem('company_name');

      if (companyId) {
        setBranchData(prevData => ({
          ...prevData,
          company_id: companyId,
          company_name: companyName
        }));
      }
    } catch (error) {
      console.error('Error fetching company details:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to fetch company details',
        icon: 'error'
      });
    }
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setBranchData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCompanyChange = (e) => {
    const selectedCompanyId = e.target.value;
    const selectedCompany = companies.find(company => company.company_id === parseInt(selectedCompanyId));
    
    setBranchData(prev => ({
      ...prev,
      company_id: selectedCompanyId,
      company_name: selectedCompany ? selectedCompany.name : ''
    }));
    console.log('Selected Company ID:', selectedCompanyId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!branchData.branch_name || !branchData.branch_location || !branchData.branch_city || !branchData.company_id) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please fill in all required fields',
        icon: 'error'
      });
      setIsLoading(false);
      return;
    }

    console.log('Branch Data being posted:', branchData);

    try {
      const response1 = await fetch(`${API_URL}/branch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(branchData),
      });

      const response2 = await fetch(`${API_URL}/branches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(branchData),
      });

      if (!response1.ok || !response2.ok) {
        throw new Error('Failed to create branch');
      }

      const responseData = await response1.json();
      await AsyncStorage.setItem('branch_id', responseData.branch_id);
      await AsyncStorage.setItem('branch_name', branchData.branch_name);

      Swal.fire({
        title: 'Success!',
        text: 'Branch created successfully',
        icon: 'success'
      });

      setBranchData(prev => ({
        ...prev,
        branch_name: '',
        branch_location: '',
        branch_location_notes: '',
        branch_city: '',
      }));

    } catch (error) {
      console.error('Error creating branch:', error);
      Swal.fire({
        title: 'Error!',
        text: error.message,
        icon: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AdministrationDashboard>
      <div className="create-branch-container">
        <Card className="shadow-sm">
          <Card.Header 
            className="bg-purple text-white py-3"
            style={{ backgroundColor: '#B03F82' }}
          >
            <div className="d-flex align-items-center">
              <GitBranch size={24} className="me-2" />
              <h5 className="mb-0" >Create New Branch</h5>
            </div>
          </Card.Header>

          <Card.Body className="p-4">
            <Form onSubmit={handleSubmit}>
              {/* Basic Information */}
              <section className="mb-4" style={{textAlign: "start"}}>
                <h6 className="mb-3 text-purple fw-bold">Basic Information</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Branch Name*</Form.Label>
                      <Form.Control
                        type="text"
                        name="branch_name"
                        value={branchData.branch_name}
                        onChange={handleChange}
                        required
                        placeholder="Enter branch name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Company Name*</Form.Label>
                      <Form.Select
                        name="company_id"
                        value={branchData.company_id}
                        onChange={handleCompanyChange}
                        required
                      >
                        <option value="">Select a company</option>
                        {companies.map(company => (
                          <option key={company.company_id} value={company.company_id}>
                            {company.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              {/* Location Information */}
              <section className="mb-4" style={{textAlign: "start"}}>
                <h6 className="mb-3 text-purple fw-bold">Location Information</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Branch Location*</Form.Label>
                      <Form.Control
                        type="text"
                        name="branch_location"
                        value={branchData.branch_location}
                        onChange={handleChange}
                        required
                        placeholder="Enter branch location"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>City*</Form.Label>
                      <Form.Control
                        type="text"
                        name="branch_city"
                        value={branchData.branch_city}
                        onChange={handleChange}
                        required
                        placeholder="Enter city"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Location Notes</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="branch_location_notes"
                        value={branchData.branch_location_notes}
                        onChange={handleChange}
                        placeholder="Additional location details"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              {/* Contact Information */}
              <section className="mb-4" style={{textAlign: "start"}}>
                <h6 className="mb-3 text-purple fw-bold">Contact Information</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Phone Number</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone"
                        value={branchData.phone}
                        onChange={handleChange}
                        placeholder="Enter phone number"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={branchData.email}
                        onChange={handleChange}
                        placeholder="Enter email address"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              {/* Inventory Settings */}
              <section className="mb-4" style={{textAlign: "start"}}>
                <h6 className="mb-3 text-purple fw-bold">Inventory Settings</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Inventory Level (kgs)</Form.Label>
                      <Form.Control
                        type="number"
                        name="inventory_level"
                        value={branchData.inventory_level}
                        onChange={handleChange}
                        placeholder="Enter inventory level"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Storage Capacity (kgs)</Form.Label>
                      <Form.Control
                        type="number"
                        name="inventory_storage_capacity"
                        value={branchData.inventory_storage_capacity}
                        onChange={handleChange}
                        placeholder="Enter storage capacity"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Reorder Level (kgs)</Form.Label>
                      <Form.Control
                        type="number"
                        name="branch_reorder_level_kgs"
                        value={branchData.branch_reorder_level_kgs}
                        onChange={handleChange}
                        placeholder="Enter reorder level"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        name="auto_branch_reorder_status"
                        checked={branchData.auto_branch_reorder_status}
                        onChange={handleChange}
                        label="Enable Auto Reorder"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              {/* Additional Settings */}
              <section className="mb-4" style={{textAlign: "start"}}>
                <h6 className="mb-3 text-purple fw-bold">Additional Settings</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Sync Interval</Form.Label>
                      <Form.Select
                        name="sync_interval"
                        value={branchData.sync_interval}
                        onChange={handleChange}
                      >
                        <option value="">Select Interval</option>
                        <option value="15">Every 15 minutes</option>
                        <option value="30">Every 30 minutes</option>
                        <option value="60">Every hour</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Transaction Volume Limit (kgs)</Form.Label>
                      <Form.Control
                        type="number"
                        name="transactions_volume_limit_kgs"
                        value={branchData.transactions_volume_limit_kgs}
                        onChange={handleChange}
                        placeholder="Enter transaction limit"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              <div className="d-flex justify-content-end mt-4">
                <Button 
                  variant="secondary" 
                  className="me-2"
                  onClick={() => window.history.back()}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  style={{ backgroundColor: '#B03F82', borderColor: '#B03F82' }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Creating...
                    </>
                  ) : (
                    'Create Branch'
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>

      <style jsx>{`
        .text-purple {
          color: #B03F82;
        }
        .create-branch-container {
          max-width: 1200px;
          margin: 0 auto;
        }
        .form-control:focus, .form-select:focus {
          border-color: #B03F82;
          box-shadow: 0 0 0 0.25rem rgba(111, 66, 193, 0.25);
        }
      `}</style>
    </AdministrationDashboard>
  );
};

export default AdminCreateBranch;